import React, { useState, useEffect, createContext, useContext } from 'react';
import { Calendar, Users, MessageSquare, Clock, MapPin, Send, Check, X, Filter, Search, Globe } from 'lucide-react';
import translations from './translations.json';

// Configuration à modifier selon votre environnement
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://matchmaking.mobivent.fr/api';
const CURRENT_USER_ID = 'recXXXXXXXXXXXXXX'; // À remplacer par l'ID utilisateur connecté
const CURRENT_SUBEVENT_ID = 'recYYYYYYYYYYYYYY'; // À remplacer par le sub-event actif

// Context pour la langue
const LanguageContext = createContext();

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => useContext(LanguageContext);

const MatchmakingDashboard = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('matches');
  const [matches, setMatches] = useState([]);
  const [meetingRequests, setMeetingRequests] = useState([]);
  const [myMeetings, setMyMeetings] = useState([]);
  const [subEvent, setSubEvent] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState(0);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);

  // Charger les données depuis l'API
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Charger le sub-event
        const subEventRes = await fetch(`${API_BASE_URL}/subevent/${CURRENT_SUBEVENT_ID}`);
        const subEventData = await subEventRes.json();
        if (subEventData.success) {
          setSubEvent(subEventData.data);
        }

        // Charger les matches
        const matchesRes = await fetch(`${API_BASE_URL}/matches/${CURRENT_USER_ID}?subevent=${CURRENT_SUBEVENT_ID}`);
        const matchesData = await matchesRes.json();
        if (matchesData.success) {
          setMatches(matchesData.data);
        }

        // Charger les demandes de meeting
        const requestsRes = await fetch(`${API_BASE_URL}/requests/${CURRENT_USER_ID}`);
        const requestsData = await requestsRes.json();
        if (requestsData.success) {
          setMeetingRequests(requestsData.data);
        }

        // Charger les meetings confirmés
        const meetingsRes = await fetch(`${API_BASE_URL}/meetings/${CURRENT_USER_ID}`);
        const meetingsData = await meetingsRes.json();
        if (meetingsData.success) {
          setMyMeetings(meetingsData.data);
        }

        // Charger les slots disponibles
        const slotsRes = await fetch(`${API_BASE_URL}/slots/${CURRENT_SUBEVENT_ID}`);
        const slotsData = await slotsRes.json();
        if (slotsData.success) {
          setAvailableSlots(slotsData.data);
        }

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredMatches = matches.filter(match => 
    match.score >= filterScore &&
    (match.other_user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     match.company?.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSendRequest = async () => {
    if (selectedSlots.length === 0) {
      alert(t('request_modal.select_slots_error') || 'Veuillez sélectionner au moins un créneau');
      return;
    }

    const message = document.getElementById('request-message').value;

    try {
      const response = await fetch(`${API_BASE_URL}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          match_id: selectedMatch.id,
          requester_id: CURRENT_USER_ID,
          recipient_id: selectedMatch.other_user.id,
          subevent_id: CURRENT_SUBEVENT_ID,
          slot_ids: selectedSlots,
          message: message
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(t('request_modal.request_sent'));
        setShowRequestModal(false);
        setSelectedSlots([]);
        // Recharger les données
        window.location.reload();
      }
    } catch (error) {
      console.error('Error sending request:', error);
      alert('Erreur lors de l\'envoi de la demande');
    }
  };

  const handleRespondToRequest = async (requestId, accepted, slotId = null) => {
    try {
      const response = await fetch(`${API_BASE_URL}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          accepted: accepted,
          response_message: accepted ? 'Accepted' : 'Declined',
          selected_slot_id: slotId
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(accepted ? t('request_card.accepted') : t('request_card.declined'));
        window.location.reload();
      }
    } catch (error) {
      console.error('Error responding to request:', error);
      alert('Erreur lors de la réponse');
    }
  };

  const toggleSlotSelection = (slotId) => {
    if (selectedSlots.includes(slotId)) {
      setSelectedSlots(selectedSlots.filter(id => id !== slotId));
    } else {
      if (selectedSlots.length < 3) {
        setSelectedSlots([...selectedSlots, slotId]);
      } else {
        alert(t('request_modal.max_slots') || 'Maximum 3 créneaux');
      }
    }
  };

  const ScoreBadge = ({ score }) => {
    const getColor = (score) => {
      if (score >= 80) return 'bg-green-100 text-green-800 border-green-300';
      if (score >= 60) return 'bg-blue-100 text-blue-800 border-blue-300';
      return 'bg-gray-100 text-gray-800 border-gray-300';
    };
    
    return (
      <div className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${getColor(score)}`}>
        {score}{t('match_card.match_percent')}
      </div>
    );
  };

  const LanguageSelector = () => (
    <div className="flex items-center gap-2">
      <Globe size={20} className="text-gray-600" />
      <select
        value={language}
        onChange={(e) => changeLanguage(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="fr">🇫🇷 Français</option>
        <option value="de">🇩🇪 Deutsch</option>
        <option value="en">🇬🇧 English</option>
      </select>
    </div>
  );

  const MatchCard = ({ match }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-l-4 border-blue-500">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-600">
            {match.other_user.name.charAt(0)}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{match.other_user.name}</h3>
            <p className="text-gray-600">{match.other_user.job_title}</p>
            <p className="text-blue-600 font-semibold">{match.company?.name}</p>
          </div>
        </div>
        <ScoreBadge score={match.score} />
      </div>
      
      <div className="mb-4">
        <p className="text-sm text-gray-700 mb-2">
          <span className="font-semibold">{t('match_card.why_match')}</span> {match.reason}
        </p>
        {match.other_user.bio && (
          <p className="text-sm text-gray-600 mt-2 italic">{match.other_user.bio}</p>
        )}
      </div>

      <div className="flex gap-2">
        <button 
          onClick={() => {
            setSelectedMatch(match);
            setShowRequestModal(true);
          }}
          disabled={match.status !== 'Pending'}
          className={`flex-1 px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 font-semibold ${
            match.status === 'Pending' 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          <Send size={18} />
          {match.status === 'Pending' ? t('match_card.request_meeting') : t('match_card.already_requested')}
        </button>
      </div>
    </div>
  );

  const RequestCard = ({ request }) => (
    <div className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
      request.type === 'received' ? 'border-green-500' : 'border-orange-500'
    }`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
              request.type === 'received' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-orange-100 text-orange-700'
            }`}>
              {request.type === 'received' ? t('request_card.received') : t('request_card.sent')}
            </span>
          </div>
          <h3 className="text-lg font-bold text-gray-900">{request.other_user.name}</h3>
          <p className="text-sm text-gray-600">{request.company}</p>
        </div>
        <span className="text-sm text-gray-500">{request.created_date}</span>
      </div>

      <div className="mb-3">
        <p className="text-sm text-gray-700 mb-2">
          <span className="font-semibold">{t('request_card.message')}</span> {request.message}
        </p>
        <div className="mt-2">
          <p className="text-xs text-gray-600 font-semibold mb-1">{t('request_card.proposed_slots')}</p>
          {request.preferred_slots.map((slot, idx) => (
            <span key={idx} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs mr-2 mb-1">
              <Clock size={12} className="inline mr-1" />
              {slot.date} {slot.start_time}-{slot.end_time}
            </span>
          ))}
        </div>
      </div>

      {request.type === 'received' && request.status === 'Pending' && (
        <div className="flex gap-2">
          <button 
            onClick={() => handleRespondToRequest(request.id, true, request.preferred_slots[0]?.id)}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
          >
            <Check size={18} />
            {t('request_card.accept')}
          </button>
          <button 
            onClick={() => handleRespondToRequest(request.id, false)}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
          >
            <X size={18} />
            {t('request_card.refuse')}
          </button>
        </div>
      )}
      
      {request.type === 'sent' && (
        <div className="text-sm text-gray-600 italic">{t('request_card.waiting_response')}</div>
      )}
    </div>
  );

  const MeetingCard = ({ meeting }) => (
    <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-purple-500">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{meeting.other_user.name}</h3>
          <p className="text-sm text-gray-600">{meeting.company}</p>
        </div>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
          {t('meeting_card.confirmed')}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <Calendar size={16} className="text-purple-600" />
          <span>{meeting.slot?.date}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Clock size={16} className="text-purple-600" />
          <span>{meeting.slot?.start_time}-{meeting.slot?.end_time}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <MapPin size={16} className="text-purple-600" />
          <span>{t('meeting_card.table')} {meeting.table?.id}</span>
        </div>
      </div>
    </div>
  );

  const RequestModal = () => {
    // Grouper les slots par jour
    const slotsByDay = availableSlots.reduce((acc, slot) => {
      const day = slot.day_number || 1;
      if (!acc[day]) acc[day] = [];
      acc[day].push(slot);
      return acc;
    }, {});

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4">{t('request_modal.title')}</h2>
          <p className="text-gray-600 mb-4">
            {t('request_modal.with')} <span className="font-semibold">{selectedMatch?.other_user.name}</span> - {selectedMatch?.company?.name}
          </p>
          
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              {t('request_modal.preferred_slots')} ({selectedSlots.length}/3)
            </label>
            <div className="space-y-4">
              {Object.entries(slotsByDay).map(([day, slots]) => (
                <div key={day}>
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">
                    {t('request_modal.day')} {day}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {slots.map((slot) => (
                      <label 
                        key={slot.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer border-2 transition ${
                          selectedSlots.includes(slot.id)
                            ? 'bg-blue-50 border-blue-500'
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <input 
                          type="checkbox" 
                          className="w-4 h-4"
                          checked={selectedSlots.includes(slot.id)}
                          onChange={() => toggleSlotSelection(slot.id)}
                        />
                        <span className="text-sm">
                          {slot.start_time} - {slot.end_time}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">{t('request_modal.message_label')}</label>
            <textarea 
              id="request-message"
              className="w-full p-3 border border-gray-300 rounded-lg text-sm"
              rows="3"
              placeholder={t('request_modal.message_placeholder')}
            />
          </div>

          <div className="flex gap-2">
            <button 
              onClick={() => {
                setShowRequestModal(false);
                setSelectedSlots([]);
              }}
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('request_modal.cancel')}
            </button>
            <button 
              onClick={handleSendRequest}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              {t('request_modal.send')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('app.title')}</h1>
              <p className="text-gray-600">{subEvent?.name || 'Loading event...'}</p>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <div className="text-right">
                <p className="text-sm text-gray-600">{t('app.connected_as')}</p>
                <p className="font-semibold">Adrien Waziers</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                A
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('matches')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'matches'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="inline mr-2" size={20} />
              {t('tabs.matches')} ({matches.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'requests'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MessageSquare className="inline mr-2" size={20} />
              {t('tabs.requests')} ({meetingRequests.length})
            </button>
            <button
              onClick={() => setActiveTab('meetings')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'meetings'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="inline mr-2" size={20} />
              {t('tabs.meetings')} ({myMeetings.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'matches' && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder={t('filters.search_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={20} className="text-gray-600" />
                  <select
                    value={filterScore}
                    onChange={(e) => setFilterScore(Number(e.target.value))}
                    className="px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={0}>{t('filters.all_scores')}</option>
                    <option value={80}>{t('filters.excellent')}</option>
                    <option value={60}>{t('filters.good')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {filteredMatches.length > 0 ? (
                filteredMatches.map(match => (
                  <MatchCard key={match.id} match={match} />
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  {t('matches.no_results') || 'Aucun match trouvé'}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="grid gap-4">
            {meetingRequests.length > 0 ? (
              meetingRequests.map(request => (
                <RequestCard key={request.id} request={request} />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                {t('requests.no_results') || 'Aucune demande'}
              </div>
            )}
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="grid gap-4">
            {myMeetings.length > 0 ? (
              myMeetings.map(meeting => (
                <MeetingCard key={meeting.id} meeting={meeting} />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                {t('meetings.no_results') || 'Aucun meeting confirmé'}
              </div>
            )}
          </div>
        )}
      </div>

      {showRequestModal && <RequestModal />}
    </div>
  );
};

const App = () => (
  <LanguageProvider>
    <MatchmakingDashboard />
  </LanguageProvider>
);

export default App;